/**
 * The insight's ⚡ line: exactly what the Hour Optimizer adds with the
 * settings being edited — the planner's own placement — or why it adds
 * fewer than asked (no room between the drops, no other hour it may use).
 * Off, it says what switching it on would add. No secret (user, 12/10/2026).
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC, ReactNode } from 'react';
import { AlertTriangle, Zap } from 'lucide-react';

import { minuteLabel } from '../hour-chart-math';
import type { ExtrasPreview } from './extras-preview';

const Mono: FC<{ children: ReactNode }> = ({ children }) => <b className="font-mono">{children}</b>;
const at = (m: number) => minuteLabel(((m % 1440) + 1440) % 1440);
const times = (p: ExtrasPreview) => p.extras.map((e) => at(e.minute)).join(' · ');

/** Why none (or not all) fit, with what to change. */
function shortfall(p: ExtrasPreview): string {
  if (p.room === null) return 'no hour of its hours is ranked, inside the window and free of a rule keeping extras out';
  if (p.room < p.gap)
    return p.room === 0
      ? `its drops leave no free minute and Space between needs ${p.gap} min`
      : `the most room between its drops is ${p.room} min and Space between needs ${p.gap} — set it to ${p.room} or space the drops out`;
  return p.usableHours > p.extras.length ? `the other hours have less than ${p.gap} min of room` : 'that is every ranked hour it may use';
}

function sentence(p: ExtrasPreview, on: boolean): ReactNode {
  const n = p.extras.length;
  const test = p.probe ? <> + the test drop at <Mono>{at(p.probe.minute)}</Mono></> : null;
  if (p.max === 0) return on && p.probe ? <>Extra drops is 0 — only the test drop, at <Mono>{at(p.probe.minute)}</Mono>.</> : <>Extra drops is 0{on ? ' — nothing is added' : ''}.</>;
  if (n === 0) return <>{on ? 'No extra drop fits' : 'no extra drop would fit'}: {shortfall(p)}.{p.probe ? <> Only the test drop, at <Mono>{at(p.probe.minute)}</Mono>.</> : null}</>;
  const list = <Mono>{times(p)}</Mono>;
  const head = on
    ? n === p.max ? <>Its extra drops today: {list}</> : <>{n} of {p.max} extra drops fit: {list}</>
    : n === p.max ? <>switched on, it would add {list}</> : <>switched on, {n} of {p.max} would fit: {list}</>;
  return (
    <>
      {head}
      {test}
      {n < p.max ? <> — {shortfall(p)}</> : null}.
    </>
  );
}

export const ExtrasLine: FC<{ preview: ExtrasPreview; on: boolean }> = ({ preview, on }) => {
  const none = preview.max > 0 && preview.extras.length === 0;
  const Icon = none ? AlertTriangle : Zap;
  return (
    <p className="flex items-start gap-2 text-[13px] leading-snug" data-testid="extras-line">
      <Icon className={`mt-0.5 h-4 w-4 shrink-0 ${none ? 'text-warning' : 'fill-primary/30 text-primary'}`} aria-hidden />
      <span>
        {on ? null : <span className="text-muted-foreground">Off — </span>}
        {sentence(preview, on)}
      </span>
    </p>
  );
};
